import { createContext, useEffect, useReducer, useState } from "react";
import { jwtDecode } from "jwt-decode";
import axios from 'axiosConfig';
import config from 'config';
// GLOBAL CUSTOM COMPONENTS
import Loading from "app/components/MatxLoading";


const initialState = {
  user: null,
  isInitialized: false,
  isAuthenticated: false,
  error: null // Adding an error state
};

const isValidToken = (accessToken) => {
  if (!accessToken) return false;
  const decodedToken = jwtDecode(accessToken);

  const currentTime = Date.now() / 1000;
  return decodedToken.exp > currentTime;

};

const setSession = (accessToken) => {
  if (accessToken) {
    localStorage.setItem("accessToken", accessToken);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem("accessToken");
    delete axios.defaults.headers.common.Authorization;
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case "INIT": {
      const { isAuthenticated, user } = action.payload;
      return { ...state, user, isAuthenticated, isInitialized: true };
    }
    case "LOGIN": {
      const { user, orgName } = action.payload;
      return { ...state, user, orgName, isAuthenticated: true, error: null };
    }
    case "LOGOUT": {
      return { ...state, isAuthenticated: false, user: null, error: null };
    }
    case "REGISTER": {
      const { user } = action.payload;
      return { ...state, isAuthenticated: true, user };
    }
    case "ERROR":
      return { ...state, error: action.error }; // Handling errors
    default: {
      return state;
    }
  }
};

const AuthContext = createContext({
  ...initialState,
  method: "JWT",
  logout: () => { },
  login: () => Promise.resolve(),
  register: () => Promise.resolve()
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  // const login = async (email, password) => {
  //   const { data } = await axios.post("/api/users/login", { username: email, password });
  //   if (data.success) {
  //     const { token, user } = data;

  //     setSession(token);
  //     localStorage.setItem("user", JSON.stringify(user));
  //     dispatch({ type: "LOGIN", payload: { user } });
  //   }
  // };

  const login = async (email, password) => {
    dispatch({ type: "ERROR", error: null });
    try {
      const { data } = await axios.post("/api/users/login", { username: email, password });
      if (data.success) {
        const { token, user, orgName } = data;
        setSession(token);
        localStorage.setItem("user", JSON.stringify(user));
        dispatch({ type: "LOGIN", payload: { isAuthenticated: true, user, orgName } });
      } else {
        throw new Error("Incorrect username or password"); // Throwing an error if login fails
      }
    } catch (error) {
      dispatch({ type: "ERROR", error: error.message });
    }
  };


  const register = async (email, username, password) => {
    const { data } = await axios.post("/api/auth/register", { email, username, password });
    const { accessToken, user } = data;

    setSession(accessToken);
    dispatch({ type: "REGISTER", payload: { user } });
  };

  const logout = () => {
    setSession(null);
    localStorage.removeItem("user");
    dispatch({ type: "LOGOUT" });
  };

  useEffect(() => {
    (async () => {
      try {
        const accessToken = window.localStorage.getItem("accessToken");
        const storedUser = localStorage.getItem("user");

        if (accessToken && isValidToken(accessToken) && storedUser) {
          setSession(accessToken);
          const user = JSON.parse(storedUser);

          dispatch({
            type: "INIT",
            payload: { isAuthenticated: true, user }
          });
        } else {
          dispatch({
            type: "INIT",
            payload: { isAuthenticated: false, user: null }
          });
        }
      } catch (err) {
        dispatch({
          type: "INIT",
          payload: { isAuthenticated: false, user: null }
        });
      }
    })();
  }, []);

  if (!state.isInitialized) return <Loading />;

  return (
    <AuthContext.Provider value={{ ...state, method: "JWT", login, logout, register }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
